import Box from "@mui/material/Box";
import { Building2, DollarSign, Globe } from "lucide-react";
import StatisticCard from "../../../../components/Statistics/StatisticCard";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  OWNER_AD_SPEND_KPI_ADVERTISERS,
  OWNER_AD_SPEND_KPI_COUNTRIES,
  OWNER_AD_SPEND_KPI_TOTAL,
  OWNER_AD_SPEND_TREND_NEW,
  OWNER_AD_SPEND_VS_PREVIOUS_PERIOD,
} from "../../../../i18n/keysTranslations";
import { PLATFORMS_SUPPORTED } from "../../../../utils/constants";
import { getIntlLocaleCode } from "../../../../utils/i18nLanguage";
import {
  AD_SPEND_CARD_SX,
  formatAdSpendPercent,
  formatAdSpendUsd,
} from "./adSpendDisplay";

const growthOf = (trend) => {
  if (!trend || trend.isNew || trend.direction === "flat") return null;
  return trend.deltaPercent;
};

const changeTextOf = ({
  trend,
  comparisonLabelKey,
  previousValue,
  formatPrevious,
  t,
}) => {
  if (trend?.isNew) return t(OWNER_AD_SPEND_TREND_NEW);
  if (comparisonLabelKey === OWNER_AD_SPEND_VS_PREVIOUS_PERIOD) {
    return t(OWNER_AD_SPEND_VS_PREVIOUS_PERIOD, {
      value: formatPrevious(previousValue),
    });
  }
  return comparisonLabelKey ? t(comparisonLabelKey) : "";
};

const sparklineTrendOf = (trend) => {
  if (trend?.direction === "up") return "up";
  if (trend?.direction === "down") return "down";
  return "stable";
};

const AdSpendKpiCards = ({
  kpis = null,
  chartData = [],
  comparisonLabelKey = "",
  showComparison = false,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslationApp();
  const language = i18n.language;
  const formatCompactUsd = (value) =>
    formatAdSpendUsd({ value, language, compact: true });
  const formatCount = (value) =>
    new Intl.NumberFormat(getIntlLocaleCode(language)).format(
      Number(value) || 0
    );
  const formatGrowth = (percent) => formatAdSpendPercent({ percent, language });
  const trend = kpis?.totalTrend ?? null;
  const sparklineValues = chartData.map((row) =>
    PLATFORMS_SUPPORTED.reduce(
      (sum, platform) => sum + (Number(row[platform]) || 0),
      0
    )
  );
  const comparisonText = ({
    trend: cardTrend,
    previousValue,
    formatPrevious,
  }) =>
    showComparison
      ? changeTextOf({
          trend: cardTrend,
          comparisonLabelKey,
          previousValue,
          formatPrevious,
          t,
        })
      : "";

  const cards = [
    {
      label: t(OWNER_AD_SPEND_KPI_TOTAL),
      value: kpis?.totalSpendUSD ?? 0,
      valueFormatter: formatCompactUsd,
      icon: DollarSign,
      growth: showComparison ? growthOf(trend) : null,
      changeText: comparisonText({
        trend,
        previousValue: kpis?.previousTotalSpendUSD,
        formatPrevious: formatCompactUsd,
      }),
      sparklineValues,
      sparklineTrend: showComparison ? sparklineTrendOf(trend) : "stable",
    },
    {
      label: t(OWNER_AD_SPEND_KPI_ADVERTISERS),
      value: kpis?.advertisersWithSpend ?? 0,
      icon: Building2,
      growth: showComparison ? growthOf(kpis?.advertisersTrend) : null,
      changeText: comparisonText({
        trend: kpis?.advertisersTrend,
        previousValue: kpis?.previousAdvertisersWithSpend,
        formatPrevious: formatCount,
      }),
    },
    {
      label: t(OWNER_AD_SPEND_KPI_COUNTRIES),
      value: kpis?.countriesWithSpend ?? 0,
      icon: Globe,
      growth: showComparison ? growthOf(kpis?.countriesTrend) : null,
      changeText: comparisonText({
        trend: kpis?.countriesTrend,
        previousValue: kpis?.previousCountriesWithSpend,
        formatPrevious: formatCount,
      }),
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
        },
        gap: 1,
        width: "100%",
      }}
    >
      {cards.map((card) => (
        <StatisticCard
          key={card.label}
          label={card.label}
          value={card.value}
          valueFormatter={card.valueFormatter}
          icon={card.icon}
          growth={card.growth}
          growthFormatter={formatGrowth}
          changeText={card.changeText}
          sparklineValues={card.sparklineValues}
          sparklineTrend={card.sparklineTrend}
          isLoading={isLoading}
          sx={AD_SPEND_CARD_SX}
        />
      ))}
    </Box>
  );
};

export default AdSpendKpiCards;
